
import { IncrediNote } from 'incredi-ui';
import { mapGetters, useStore } from 'vuex';
import { useNotifyPresets } from '@/components/agents-table/services/notifyPresets';
import { defineComponent, inject } from 'vue';
import { Action_Type, Agent } from '@/graphql/codegen/generated/graphql';
import { useDialogPresets } from '@/services/dialogPresets.composable';
import * as utils from '@/services/utils';
import { useAgentsActions } from '@/views/agents/agents.helpers';
import { RegistryKeyEnums } from '@/components/agents-table/AgentActionMenu.types';
import { IKCoordinatorsService } from '@/modules/CoordinatorsService/CoordinatorService.InjectionKeys';
import { validateStandartCharacters } from '@/utils/string-validations';
import COLORS from '@/design-system/colors.json';
import { ResponseStatus } from '../../../const/globals';
import IncrediSelectMenu from '../../inputs/incrediSelectMenu.vue';
import IncrediInputNumWithDefinedType from '../../inputs/incrediInputNumWithDefinedType.vue';
import IncrediTextInput from '../../inputs/incrediTextInput.vue';
import IncrediToggle from '../../inputs/incrediToggle.vue';
import InputErrorMessage from '../inputErrorMessage.vue';

export default defineComponent({
  name: 'SetRegistryConfigurationDialog',
  components: {
    InputErrorMessage,
    IncrediSelectMenu,
    IncrediInputNumWithDefinedType,
    IncrediTextInput,
    IncrediToggle,
    IncrediNote,
  },

  setup() {
    const store = useStore();
    const coordinatorsService = inject(IKCoordinatorsService);
    const { notifyPresets } = useNotifyPresets();
    const { dialogPresets } = useDialogPresets();
    const { setAgentsAction } = useAgentsActions();
    return { store, notifyPresets, dialogPresets, utils, setAgentsAction, coordinatorsService, COLORS };
  },
  data() {
    return {
      selectedRegistryName: '',
      registryDescText: '',
      selectedRegistryValue: undefined as any,
      registryComponent: undefined as any,
      requestCompleted: false,
      registryMapArray: JSON.parse(this.$t('agentsTable.setRegistryKey.registryMapping')),
    };
  },
  computed: {
    ...mapGetters({
      dialogData: ['main/dialogData'] as any,
    }),
    shouldEnableSubmitButton() {
      return this.selectedRegistryValue !== undefined && this.selectedRegistryValue !== '';
    },
    registryOptionsNames() {
      return this.registryMapArray.map((item: any) => item.displayName).sort();
    },
    showRegistryComponent() {
      return this.registryDescText.length > 0 && this.registryComponent && !this.requestCompleted;
    },
    selectedAgentsForAction() {
      return (this.dialogData.agents as Agent[]) ? (this.dialogData.agents as Agent[]) : [];
    },
    showMaxParallelLinkTargetsNote() {
      const isBulk = this.selectedAgentsForAction.length > 1;
      return isBulk && this.showRegistryComponent && this.getRegistryKeyByDisplayName() === 'MaxParallelLinkTargets';
    },
    registryComponentProps() {
      const registryKey = this.getRegistryKeyByDisplayName();
      switch (registryKey) {
        case 'CoordHost':
          return {
            toBeClearable: false,
            step: 'any',
            rules: [(val: string | number) => !val || String(val).length >= 1],
            lazyRules: true,
          };
        case 'BuildMonitorOutputFont':
          return {
            toBeClearable: false,
            step: 'any',
            inputType: 'text',
            rules: [(val: any) => !val || val.length > 100 || validateStandartCharacters(val) || this.$t('agentsTable.setRegistryKey.stringErrorMessage')],
            lazyRules: true,
          };
        case 'MaxLogSize':
          return {
            step: 'any', // To hide hint of float number
            inputType: 'MB',
            rules: [(val: any) => !val || this.utils.isPositiveIntegerNumber(val) || this.$t('agentsTable.setRegistryKey.positiveIntegerErrorMessage')],
            lazyRules: true,
          };
        case 'ForceCPUCount_WhenInitiator':
          return {
            step: 'any',
            inputType: '',
            rules: [(val: any) => !val || this.utils.isPositiveIntegerNumber(val) || this.$t('agentsTable.setRegistryKey.positiveIntegerErrorMessage')],
            lazyRules: true,
          };
        case 'CoordPort':
          return {
            step: 'any',
            inputType: '',
            rules: [
              (val: any) =>
                !val || (this.utils.isNumberInRange(1024, 65535, val) && this.utils.isPositiveIntegerNumber(val)) || this.registryMapArray.find((item: any) => item.key === 'CoordPort').errorMessage,
            ],
            lazyRules: true,
          };
        case 'HelperActivityReportTimeout':
          return {
            step: 'any',
            inputType: 'sec',
            rules: [(val: any) => !val || this.utils.isPositiveIntegerNumber(val) || this.$t('agentsTable.setRegistryKey.positiveIntegerErrorMessage')],
            lazyRules: true,
          };
        // new values WIN-14964
        case 'MultiThreadedFileSyncThreads':
          return {
            step: 'any',
            inputType: '',
            rules: [(val: any) => this.utils.isNumberInRange(1, 50, val) || this.registryMapArray.find((item: any) => item.key === 'MultiThreadedFileSyncThreads').errorMessage],
            lazyRules: true,
          };
        case 'MaxConcurrentPDBs':
          return {
            step: 'any',
            inputType: '',
            rules: [(val: any) => this.utils.isNumberInRange(0, 100, val) || this.registryMapArray.find((item: any) => item.key === 'MaxConcurrentPDBs').errorMessage],
            lazyRules: true,
          };
        case 'MaxParallelLinkTargets':
          return {
            step: 'any',
            inputType: '',
            rules: [
              (val: any) =>
                this.selectedAgentsForAction.length === 1
                  ? this.utils.isNumberInRange(1, this.getSelectedAgentLogicalCores() || 0, val) ||
                    this.registryMapArray.find((item: any) => item.key === 'MaxParallelLinkTargets').errorMessage.single + this.getSelectedAgentLogicalCores()
                  : this.utils.isPositiveNumber(val) || this.registryMapArray.find((item: any) => item.key === 'MaxParallelLinkTargets').errorMessage.bulk,
            ],
            lazyRules: true,
          };
        case 'Use64bitToolset':
        case 'UseMsBuild':
        case 'ForceEnglishMSBuildOutput':
        case 'AllowParallelLink':
        case 'AvoidLocalExec':
        case 'PredictedExecutionMode':
        case 'OnlyFailLocally':
          return {
            toggleOnText: 'Enabled',
            toggleOffText: 'Disabled',
          };
        default:
          break;
      }
      return '';
    },
  },
  created() {
    this.registryMapArray = JSON.parse(this.$t('agentsTable.setRegistryKey.registryMapping'));
  },
  methods: {
    handleSelectMenuChange(value: any) {
      const dynamicComponentsKeyToComponentName = {
        CoordHost: 'IncrediTextInput',
        CoordPort: 'IncrediInputNumWithDefinedType',
        MaxLogSize: 'IncrediInputNumWithDefinedType',
        OnlyFailLocally: 'IncrediToggle',
        UseMsBuild: 'IncrediToggle',
        Use64bitToolset: 'IncrediToggle',
        ForceCPUCount_WhenInitiator: 'IncrediTextInput',
        HelperActivityReportTimeout: 'IncrediInputNumWithDefinedType',
        MultiThreadedFileSyncThreads: 'IncrediInputNumWithDefinedType',
        ForceEnglishMSBuildOutput: 'IncrediToggle',
        AllowParallelLink: 'IncrediToggle',
        MaxParallelLinkTargets: 'IncrediInputNumWithDefinedType',
        AvoidLocalExec: 'IncrediToggle',
        MaxConcurrentPDBs: 'IncrediInputNumWithDefinedType',
        PredictedExecutionMode: 'IncrediToggle',
        BuildMonitorOutputFont: 'IncrediTextInput',
      } as any;
      this.selectedRegistryName = value;
      // reset prev choice
      this.selectedRegistryValue = undefined;
      // select relevant component
      const matchItem = this.registryMapArray.find((item: any) => item.displayName === value);
      this.registryDescText = matchItem.description;
      this.registryComponent = dynamicComponentsKeyToComponentName[matchItem.key];
    },
    handleRegistryValueChange(value: any) {
      this.selectedRegistryValue = value;
      if ((this.$refs.myInput as any).$refs['q-input']) (this.$refs.myInput as any).$refs['q-input'].resetValidation();
    },
    getRegistryKeyByDisplayName() {
      const name = this.selectedRegistryName;
      const matchItem = this.registryMapArray.find((item: any) => item.displayName === name);
      return matchItem?.key || '';
    },
    // relevant only for registry MaxParallelLinkTargets and single action
    getSelectedAgentLogicalCores() {
      return this.selectedAgentsForAction.length === 1 ? this.selectedAgentsForAction[0].performance?.cpuLogical : 0;
    },
    handleSubmitConfiguration() {
      (this.$refs.registryConfigurationForm as any).validate().then(async (success: boolean) => {
        if (success) {
          const isBulk = this.selectedAgentsForAction.length > 1;
          this.store.commit('main/dialogState', false);
          const arrIntegerValue = [
            'CoordPort',
            'MaxLogSize',
            'HelperActivityReportTimeout',
            'ForceCPUCount_WhenInitiator',
            'MultiThreadedFileSyncThreads',
            'MaxParallelLinkTargets',
            'MaxConcurrentPDBs',
          ];
          const value = arrIntegerValue.includes(this.getRegistryKeyByDisplayName()) ? parseInt(this.selectedRegistryValue) : this.selectedRegistryValue;
          const key = this.getRegistryKeyByDisplayName();

          const results = await this.setAgentsAction(Action_Type.AdvancedSettings, {
            agents: this.selectedAgentsForAction.map(agent => ({
              agentId: agent.id as string,
              coordinatorId: this.coordinatorsService?.selectedCoordinator.value || '',
            })),
            parameter: value,
            key: RegistryKeyEnums[key],
          });
          const successCount = results?.data?.agents_action_asyncResult?.result?.filter(result => result?.status === ResponseStatus.Ok).length || 0;
          if (successCount === results?.data?.agents_action_asyncResult?.result?.length) {
            return this.notifyPresets.requestSentSuccess(results?.data?.agents_action_asyncResult?.result?.length);
          }
          if (isBulk) {
            return this.dialogPresets.agentsBulkActionPartialSuccessOrFail(
              results.data.agents_action_asyncResult?.result?.map(result => ({
                status: result?.status || '',
                agentName: result?.agentName || '',
                message: result?.message || '',
                errorCode: result?.errorCode || null,
              })) || [],
              successCount,
            );
          }
          return this.notifyPresets.operationFailed();
        }
      });
    },
  },
});
